import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Footer from '../components/minimal/Footer';
import { JarvisLogService } from '../services/jarvisLogService';
import { JarvisService } from '../services/jarvisService';
import { JarvisLogEntry } from '../types/jarvis.types';

const MinimalToday = () => {
  const [todayLog, setTodayLog] = useState<JarvisLogEntry | null>(null);
  const [historicalLogs, setHistoricalLogs] = useState<Map<string, JarvisLogEntry[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [fetchingNew, setFetchingNew] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [streamedContent, setStreamedContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [enableStreaming, setEnableStreaming] = useState(() => {
    const saved = localStorage.getItem('todayPageStreamingEnabled');
    return saved !== null ? saved === 'true' : true; // Default: enabled
  });
  const streamingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastContentRef = React.useRef<string>('');
  const contentEndRef = React.useRef<HTMLDivElement>(null);
  const userScrolledRef = React.useRef<boolean>(false);
  const autoScrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Streaming effect for content
  useEffect(() => {
    if (!todayLog?.response_content) return;

    const content = todayLog.response_content;

    // Skip if we already processed this exact content
    if (lastContentRef.current === content) {
      return;
    }

    lastContentRef.current = content;

    // Skip streaming if disabled
    if (!enableStreaming) {
      setStreamedContent(content);
      setIsStreaming(false);
      return;
    }

    // Start streaming animation
    setStreamedContent('');
    setIsStreaming(true);

    let currentIndex = 0;
    // Adjust chunk size and interval based on content length for smoother animation
    const chunkSize = Math.max(3, Math.floor(content.length / 200));
    const intervalMs = content.length > 3000 ? 3 : 5;

    const interval = setInterval(() => {
      currentIndex += chunkSize;
      if (currentIndex >= content.length) {
        setStreamedContent(content);
        setIsStreaming(false);
        clearInterval(interval);
      } else {
        setStreamedContent(content.slice(0, currentIndex));
      }
    }, intervalMs);

    streamingIntervalRef.current = interval;

    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
      }
    };
  }, [todayLog, enableStreaming]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && streamedContent && !userScrolledRef.current) {
      // Smooth scroll to the bottom of the content
      contentEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [streamedContent, isStreaming]);

  // Detect manual scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isStreaming) {
        userScrolledRef.current = true;
        // Reset after 2 seconds of no streaming
        if (autoScrollTimeoutRef.current) {
          clearTimeout(autoScrollTimeoutRef.current);
        }
        autoScrollTimeoutRef.current = setTimeout(() => {
          userScrolledRef.current = false;
        }, 2000);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
    };
  }, [isStreaming]);

  // Reset scroll flag when streaming starts
  useEffect(() => {
    if (isStreaming) {
      userScrolledRef.current = false;
    }
  }, [isStreaming]);

  const skipStreaming = () => {
    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current);
    }
    if (todayLog?.response_content) {
      setStreamedContent(todayLog.response_content);
      setIsStreaming(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load today's logs first (priority)
      const todayLogs = await JarvisLogService.getTodayLogs();
      const mostRecent = JarvisLogService.getMostRecentSuccess(todayLogs);
      setTodayLog(mostRecent);
      setLoading(false);

      // Load history in background (non-blocking)
      JarvisLogService.getLogsForLastNDays(7).then(history => {
        setHistoricalLogs(history);
      }).catch(err => {
        console.error('Error loading history:', err);
      });
    } catch (err) {
      console.error('Error loading logs:', err);
      setError('Failed to load your daily briefing. Please try again later.');
      setLoading(false);
    }
  };

  const fetchNewBriefing = async () => {
    try {
      setFetchingNew(true);
      setError(null);

      // Call JARVIS API to get a new briefing
      const response = await JarvisService.getTodayFocus();
      
      if (response.status === 'success') {
        // Convert to log entry format and update the UI immediately
        const newLog = JarvisService.convertToLogEntry(response) as JarvisLogEntry;
        setTodayLog(newLog);
        
        // Reload data to get the logged entry from the API
        // (Give it a moment for the JARVIS API to log to the logging service)
        setTimeout(() => {
          loadData();
        }, 2000);
      } else {
        setError(response.error || 'Failed to fetch new briefing');
      }
    } catch (err) {
      console.error('Error fetching new briefing:', err);
      setError('Failed to fetch new briefing. Please try again.');
    } finally {
      setFetchingNew(false);
    }
  };

  const getContentPreview = (content: string): string => {
    // Extract first 1-2 sentences or ~150 characters for preview
    const lines = content.split('\n').filter(line => line.trim() && !line.startsWith('#'));
    if (lines.length === 0) return 'No preview available';

    const firstLine = lines[0].trim();
    // Get first sentence or first 150 chars
    const preview = firstLine.split(/[.!?]/)[0] || firstLine.slice(0, 150);
    return preview.length > 150 ? preview.slice(0, 150) + '...' : preview + (firstLine.length > preview.length ? '...' : '');
  };

  const renderBriefingContent = (log: JarvisLogEntry | null, useStreaming: boolean = false) => {
    if (!log || !log.response_content) {
      return (
        <div className="text-gray-500 italic">
          No briefing available for this day.
        </div>
      );
    }

    // Use streamed content for today's view, full content for history
    const contentToRender = useStreaming ? streamedContent : log.response_content;

    // Render markdown content with proper styling
    return (
      <div className="space-y-6">
        <style>{`
          .jarvis-content h3 {
            font-size: 1.125rem;
            font-weight: 700;
            margin-top: 1.5rem;
            margin-bottom: 1rem;
            color: #111827;
          }
          .jarvis-content h3:first-child {
            margin-top: 0;
          }
          .jarvis-content p {
            margin-bottom: 1rem;
            line-height: 1.75;
            color: #374151;
          }
          .jarvis-content strong {
            font-weight: 600;
            color: #000;
          }
          .jarvis-content ul, .jarvis-content ol {
            margin-bottom: 1.25rem;
            padding-left: 1.5rem;
          }
          .jarvis-content li {
            margin-bottom: 0.5rem;
            line-height: 1.75;
            color: #374151;
          }
          .jarvis-content li ul, .jarvis-content li ol {
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .table-wrapper {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: 1.5rem 0;
          }
          .jarvis-content table {
            width: 100%;
            min-width: 500px;
            border-collapse: collapse;
            font-size: 0.875rem;
            border-radius: 0.5rem;
            border: 1px solid #e5e7eb;
          }
          .jarvis-content thead {
            background-color: #f9fafb;
          }
          .jarvis-content th {
            padding: 0.75rem 1rem;
            text-align: left;
            font-weight: 600;
            color: #111827;
            border-bottom: 2px solid #e5e7eb;
            white-space: nowrap;
          }
          .jarvis-content td {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid #e5e7eb;
            color: #374151;
          }
          .jarvis-content tbody tr:last-child td {
            border-bottom: none;
          }
          .jarvis-content tbody tr:hover {
            background-color: #f9fafb;
          }
          .jarvis-content blockquote {
            border-left: 4px solid #e5e7eb;
            padding-left: 1rem;
            margin: 1.5rem 0;
            color: #6b7280;
            font-style: italic;
          }
          .jarvis-content code {
            background-color: #f3f4f6;
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-size: 0.875em;
            font-family: ui-monospace, monospace;
          }
        `}</style>
        <div className="jarvis-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ node, ...props }) => (
                <div className="table-wrapper">
                  <table {...props} />
                </div>
              ),
            }}
          >
            {contentToRender}
          </ReactMarkdown>
          {useStreaming && isStreaming && (
            <span className="inline-block w-1 h-4 ml-1 bg-gray-900 animate-pulse"></span>
          )}
          {/* Auto-scroll anchor */}
          {useStreaming && <div ref={contentEndRef} />}
        </div>

        {/* Metadata */}
        <div className="mt-6 sm:mt-8 pt-4 border-t border-gray-200 text-xs sm:text-sm text-gray-500 space-y-1.5">
          <div>
            <span className="font-medium">Time:</span> {JarvisLogService.formatTime(log.timestamp)}
          </div>
          {log.tokens && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-medium">Tokens:</span>
              <span>{log.tokens.total_tokens.toLocaleString()}</span>
              {log.tokens.cached_tokens && log.tokens.cached_tokens > 0 && (
                <span>({log.tokens.cached_tokens.toLocaleString()} cached)</span>
              )}
            </div>
          )}
          {log.cost && (
            <div>
              <span className="font-medium">Cost:</span> ${log.cost.total_cost.toFixed(4)}
            </div>
          )}
          {log.duration_ms && (
            <div>
              <span className="font-medium">Response time:</span> {(log.duration_ms / 1000).toFixed(2)}s
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCalendarView = () => {
    if (historicalLogs.size === 0) {
      return (
        <div className="text-gray-500 text-center py-12 text-sm">
          No historical data available yet.
        </div>
      );
    }

    // Get dates that have briefings
    const datesWithBriefings = Array.from(historicalLogs.keys()).map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    });

    // Convert selected date string to Date object (parse as local date)
    const selectedDateObj = selectedDate ? (() => {
      const [year, month, day] = selectedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    })() : null;

    // Helper to format date as YYYY-MM-DD
    const formatDateKey = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Handler for date selection
    const handleDateChange = (date: Date | null) => {
      if (!date) {
        setSelectedDate(null);
        return;
      }

      const dateKey = formatDateKey(date);

      if (historicalLogs.has(dateKey)) {
        setSelectedDate(dateKey);
      }
    };

    // Custom day class name function to highlight dates with briefings
    const getDayClassName = (date: Date) => {
      const dateKey = formatDateKey(date);
      const hasBriefing = historicalLogs.has(dateKey);

      if (hasBriefing) {
        return 'has-briefing';
      }
      return '';
    };

    return (
      <div>
        {/* Calendar */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-6 mb-4">
          <style>{`
            .react-datepicker {
              font-family: inherit;
              border: none;
              width: 100%;
            }
            .react-datepicker__header {
              background-color: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
              padding: 0.75rem 0.5rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__header {
                padding: 1rem;
              }
            }
            .react-datepicker__current-month {
              font-size: 1rem;
              font-weight: 600;
              color: #111827;
            }
            @media (min-width: 640px) {
              .react-datepicker__current-month {
                font-size: 1.125rem;
              }
            }
            .react-datepicker__day-names {
              margin-top: 0.75rem;
            }
            .react-datepicker__day-name {
              color: #6b7280;
              font-size: 0.75rem;
              font-weight: 600;
              width: 2.25rem;
              line-height: 2.25rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__day-name {
                font-size: 0.875rem;
                width: 2.5rem;
                line-height: 2.5rem;
              }
            }
            .react-datepicker__month {
              margin: 0.25rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__month {
                margin: 0.5rem;
              }
            }
            .react-datepicker__day {
              width: 2.25rem;
              line-height: 2.25rem;
              margin: 0.125rem;
              border-radius: 0.5rem;
              color: #6b7280;
              font-size: 0.8rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__day {
                width: 2.5rem;
                line-height: 2.5rem;
                margin: 0.166rem;
                font-size: 0.875rem;
              }
            }
            .react-datepicker__day:hover {
              background-color: #f3f4f6;
            }
            .react-datepicker__day--disabled {
              color: #d1d5db !important;
              cursor: not-allowed !important;
            }
            .react-datepicker__day--disabled:hover {
              background-color: transparent !important;
            }
            .react-datepicker__day--outside-month {
              color: #d1d5db;
            }
            .react-datepicker__day--selected {
              background-color: #2563eb !important;
              color: white !important;
              font-weight: 600;
            }
            .react-datepicker__day--selected:hover {
              background-color: #1d4ed8 !important;
            }
            .react-datepicker__day--today {
              font-weight: 600;
              color: #2563eb;
            }
            .react-datepicker__day.has-briefing {
              background-color: #dbeafe;
              color: #1e40af;
              font-weight: 500;
              position: relative;
            }
            .react-datepicker__day.has-briefing:not(.react-datepicker__day--selected)::after {
              content: '';
              position: absolute;
              bottom: 0.25rem;
              left: 50%;
              transform: translateX(-50%);
              width: 0.25rem;
              height: 0.25rem;
              background-color: #2563eb;
              border-radius: 50%;
            }
            .react-datepicker__day.has-briefing:hover {
              background-color: #bfdbfe !important;
            }
            .react-datepicker__navigation {
              top: 1rem;
              width: 2rem;
              height: 2rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__navigation {
                top: 1.25rem;
              }
            }
            .react-datepicker__navigation--previous {
              left: 0.5rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__navigation--previous {
                left: 1rem;
              }
            }
            .react-datepicker__navigation--next {
              right: 0.5rem;
            }
            @media (min-width: 640px) {
              .react-datepicker__navigation--next {
                right: 1rem;
              }
            }
            .react-datepicker-wrapper {
              width: 100%;
            }
            .react-datepicker__input-container {
              display: none;
            }
          `}</style>

          <div className="text-xs sm:text-sm text-gray-600 mb-4">
            Select a date to view that day's briefing
          </div>

          <DatePicker
            selected={selectedDateObj}
            onChange={handleDateChange}
            dayClassName={getDayClassName}
            maxDate={new Date()}
            inline
            calendarClassName="w-full"
            filterDate={(date) => {
              const dateKey = formatDateKey(date);
              return historicalLogs.has(dateKey);
            }}
          />

          <div className="mt-4 flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span>Has briefing</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded border border-gray-300"></div>
              <span>No briefing</span>
            </div>
          </div>
        </div>

        {/* Selected date content */}
        {selectedDate && historicalLogs.has(selectedDate) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base sm:text-lg">{JarvisLogService.formatDate(selectedDate)}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedDate}</p>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-4 sm:px-6 py-6">
              {renderBriefingContent(
                JarvisLogService.getMostRecentSuccess(historicalLogs.get(selectedDate) || []),
                false
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderListView = () => {
    const sortedDates = Array.from(historicalLogs.keys()).sort((a, b) => b.localeCompare(a));

    if (sortedDates.length === 0) {
      return (
        <div className="text-gray-500 text-center py-12 text-sm">
          No historical data available yet.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {sortedDates.map(date => {
          const logs = historicalLogs.get(date) || [];
          const mostRecent = JarvisLogService.getMostRecentSuccess(logs);
          const preview = mostRecent ? getContentPreview(mostRecent.response_content) : 'No content';
          const isSelected = selectedDate === date;

          return (
            <div key={date} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setSelectedDate(isSelected ? null : date)}
                className="w-full px-4 sm:px-5 py-4 sm:py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left flex items-center justify-between group min-h-[60px]"
              >
                <div className="flex-1 min-w-0 pr-4">
                  <div className="font-semibold text-gray-900 text-sm sm:text-base">
                    {JarvisLogService.formatDate(date)}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {date} • {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
                  </div>
                  {!isSelected && (
                    <div className="text-xs sm:text-sm text-gray-600 mt-2 line-clamp-2">
                      {preview}
                    </div>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-all flex-shrink-0 ${isSelected ? 'rotate-180' : ''}`}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isSelected && (
                <div className="px-4 sm:px-5 py-5 bg-white border-t border-gray-200">
                  {renderBriefingContent(mostRecent, false)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderHistoricalView = () => {
    return viewMode === 'calendar' ? renderCalendarView() : renderListView();
  };

  return (
    <>
      <style>{`
        .today-page-wide {
          margin-left: calc(-50vw + 50%);
          margin-right: calc(-50vw + 50%);
          padding-left: max(1.5rem, calc(50vw - 600px));
          padding-right: max(1.5rem, calc(50vw - 600px));
        }
        @media (min-width: 768px) {
          .today-page-wide {
            padding-left: max(2rem, calc(50vw - 600px));
            padding-right: max(2rem, calc(50vw - 600px));
          }
        }
        @media (min-width: 1024px) {
          .today-page-wide {
            padding-left: max(3rem, calc(50vw - 700px));
            padding-right: max(3rem, calc(50vw - 700px));
          }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      <div className="today-page-wide">
        <Helmet>
          <title>Today | Abhinav Chinnusamy</title>
          <meta name="description" content="Daily briefing and productivity insights" />
        </Helmet>

        <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Today</h1>
        <p className="text-gray-600 text-sm sm:text-base">Your daily briefing and what you should focus on</p>
      </div>

      {/* Toggle between Today and History */}
      <div className="flex gap-4 mb-8 border-b border-gray-200">
        <button
          onClick={() => setShowHistory(false)}
          className={`pb-3 px-2 -mx-2 font-medium transition-all text-sm sm:text-base min-h-[44px] sm:min-h-0 flex items-center ${
            !showHistory
              ? 'text-black border-b-2 border-black -mb-[1px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Today
        </button>
        <button
          onClick={() => setShowHistory(true)}
          className={`pb-3 px-2 -mx-2 font-medium transition-all text-sm sm:text-base min-h-[44px] sm:min-h-0 flex items-center ${
            showHistory
              ? 'text-black border-b-2 border-black -mb-[1px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          History
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-900 mb-1">{error}</h3>
              <button
                onClick={loadData}
                className="text-sm text-red-700 hover:text-red-800 font-medium underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <div className="mb-12">
          {!showHistory ? (
            // Today's Briefing
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-base sm:text-lg font-semibold">Today's Focus</h2>
                    <button
                      onClick={() => {
                        const newValue = !enableStreaming;
                        setEnableStreaming(newValue);
                        localStorage.setItem('todayPageStreamingEnabled', String(newValue));
                      }}
                      className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors min-h-[44px] sm:min-h-0 px-2 -mx-2 rounded hover:bg-gray-100"
                      title={enableStreaming ? "Disable typing animation" : "Enable typing animation"}
                    >
                      <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        {enableStreaming ? (
                          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        ) : (
                          <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        )}
                      </svg>
                      <span className="hidden sm:inline">{enableStreaming ? 'Streaming on' : 'Streaming off'}</span>
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={fetchNewBriefing}
                      disabled={fetchingNew}
                      className="text-xs sm:text-sm px-3 sm:px-4 py-2.5 sm:py-2 bg-black text-white hover:bg-gray-800 disabled:bg-gray-400 rounded flex items-center gap-2 transition-colors font-medium min-h-[44px] sm:min-h-0"
                      title="Get new briefing from JARVIS"
                    >
                      {fetchingNew ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                          <span>Thinking...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span>New Briefing</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={loadData}
                      disabled={loading}
                      className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 hover:bg-gray-100 rounded transition-colors min-h-[44px] sm:min-h-0"
                      title="Refresh from logs"
                    >
                      <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="px-4 sm:px-6 py-6 relative">
                {isStreaming && (
                  <button
                    onClick={skipStreaming}
                    className="sticky top-4 float-right z-10 text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-900 text-white hover:bg-gray-700 rounded flex items-center gap-2 transition-all shadow-lg mb-4 min-h-[44px] sm:min-h-0"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                    <span>Skip</span>
                  </button>
                )}
                {renderBriefingContent(todayLog, true)}
              </div>
            </div>
          ) : (
            // Historical Briefings
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-base sm:text-lg font-semibold">Previous Days</h2>
                <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium rounded transition-all min-h-[44px] sm:min-h-0 flex items-center justify-center ${
                      viewMode === 'list'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="List view"
                  >
                    <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-2.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium rounded transition-all min-h-[44px] sm:min-h-0 flex items-center justify-center ${
                      viewMode === 'calendar'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Calendar view"
                  >
                    <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </button>
                </div>
              </div>
              {renderHistoricalView()}
            </div>
          )}
        </div>
      )}

      {/* Stats Summary */}
      {!loading && !error && historicalLogs.size > 0 && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Usage Stats (Last 7 Days)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
              <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Days</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{historicalLogs.size}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
              <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Entries</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {Array.from(historicalLogs.values()).reduce((sum, logs) => sum + logs.length, 0)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
              <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Cost</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                ${JarvisLogService.calculateTotalCost(
                  Array.from(historicalLogs.values()).flat()
                ).toFixed(3)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-100">
              <div className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Tokens</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {(JarvisLogService.calculateTotalTokens(
                  Array.from(historicalLogs.values()).flat()
                ) / 1000).toFixed(1)}k
              </div>
            </div>
          </div>
        </div>
      )}

        <Footer />
      </div>
    </>
  );
};

export default MinimalToday;
